import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Tag } from 'lucide-react';
import axios from 'axios';
import { useLang } from '../i18n/LanguageContext';
import { seoFor } from '../data/seoMeta';
import { categoryLabel, formatBlogDate } from '../lib/blogCategories';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = () => {
  const { t, lang } = useLang();
  const seo = seoFor('/blog', lang, { title: 'Blog — Conseils LED pour événements | Party LED', description: 'Conseils et guides pour animer vos événements avec des bracelets LED : mariages, festivals, soirées corporate.' });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const qs = new URLSearchParams();
        if (activeCategory) qs.set('category', activeCategory);
        qs.set('lang', lang);
        const response = await axios.get(`${BACKEND_URL}/api/public/blog/posts?${qs.toString()}`);
        setPosts(response.data);
      } catch (error) { setLoading(false); }
      finally { setLoading(false); }
    };
    const loadCategories = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/blog/categories`);
        setCategories(response.data);
      } catch (error) { setCategories([]); }
    };
    loadPosts();
    loadCategories();
  }, [activeCategory, lang]);

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href="https://www.party-led.com/blog" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:image" content="https://www.party-led.com/og-image.jpg" />
        <meta property="og:url" content="https://www.party-led.com/blog" />
        <meta property="og:type" content="website" />
      </>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12" data-testid="blog-header">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            {t.blog.title}
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t.blog.subtitle}
          </p>
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="blog-categories">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                !activeCategory
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
              data-testid="category-all"
            >
              {t.blog.all}
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
                data-testid={`category-${cat}`}
              >
                {categoryLabel(cat, lang)}
              </button>
            ))}
          </div>
        )}

        {/* Posts Grid */}
        {loading ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">{t.blog.loading}</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">{t.blog.noArticles}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-posts-grid">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}?lang=${lang}`}
                className="group"
                data-testid={`blog-card-${post.slug}`}
              >
                <article className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-purple-600/50 transition-all duration-300 h-full flex flex-col">
                  {/* Image */}
                  <div className="relative overflow-hidden aspect-video">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-purple-600/90 text-white text-xs font-medium px-3 py-1 rounded-full">
                        {categoryLabel(post.category, lang)}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                      <Calendar className="w-3.5 h-3.5" />
                      <time dateTime={post.createdAt}>
                        {formatBlogDate(post.createdAt, lang)}
                      </time>
                    </div>

                    <h2 className="text-xl font-bold text-white mb-3 group-hover:text-purple-400 transition-colors line-clamp-2">
                      {post.title}
                    </h2>

                    <p className="text-gray-400 text-sm mb-4 flex-1 line-clamp-3">
                      {post.excerpt}
                    </p>

                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="flex items-center gap-1 text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded">
                            <Tag className="w-3 h-3" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center text-purple-400 text-sm font-medium group-hover:gap-3 gap-2 transition-all mt-auto">
                      {t.blog.readMore}
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
